/** @jsxRuntime classic */
/** @jsx jsx */
import {css, jsx} from "@emotion/core";
import React, {Fragment} from 'react';
import {LayerDef} from "../types";
import {
    Button,
    Container,
    DragIndicatorVerticalIcon,
    ExpansionStatus,
    PlusCircleIcon,
    Theme,
    Toolbar,
    ToolbarSpacer
} from "@ice/components";
import {getAppState} from "../store/AppState";
import LayerPanel from "./LayerPanel";
import {getAppComponent, getMapComponent} from "../store/Accessor";
import {$t, $ts} from "../lang/LangUtil";
import {EditTextDialog} from "./EditTextDialog";
import {GuiUtil} from "../util/GuiUtil";
import {IconInfo} from "../util/IconInfo";
import {QuestionDialog} from "./QuestionDialog";
import {AddLayerDialog} from "./AddLayerDialog";
import {DragDropContext, Draggable, DraggableProvided, Droppable} from "react-beautiful-dnd";


interface LayerPanelListProps {
}


interface LayerPanelListState {
    expandedLayerDef: LayerDef;
}


export default class LayerPanelList extends React.Component<LayerPanelListProps, LayerPanelListState> {

    constructor(props: LayerPanelListProps) {
        super(props);
        const appState = getAppState();
        this.state = {
            expandedLayerDef: appState.getLayerDefById(appState.getEditedLayerId())
        }
    };

    private toggleHideLayer = (layerDef: LayerDef) => {
        layerDef.isHidden = !layerDef.isHidden;
        this.forceUpdate();
        getMapComponent().updateExistingLayer(layerDef);
    }

    private renameLayer = (layerDef: LayerDef) => {
        getAppComponent().showDialog(
            <EditTextDialog title={$t("renamelayer")} value={layerDef.name} confirmText={$t("rename")} onChange={name => {
                layerDef.name = name;
                let app = getAppComponent();
                app.hideDialog();
                app.refreshToolbox();
            }} />
        )
    }

    private deleteLayer = (layerDef: LayerDef) => {
        const appComponent = getAppComponent();
        appComponent.showDialog(
            <QuestionDialog
                title={$t("confirm")}
                text={$ts("deletelayerx", layerDef.name)}
                onConfirm={() => {
                    getAppState().deleteLayerDef(layerDef);
                    this.forceUpdate();
                    getMapComponent().deleteLayer(layerDef);
                    appComponent.hideDialog();
                }}
            />
        );
    }

    private renderLayer = (layerDef: LayerDef, provided: DraggableProvided) => {
        const isExpanded = this.state.expandedLayerDef === layerDef;

        const onClickExpand = () => {
            let isAlreadyExpanded = this.state.expandedLayerDef === layerDef;
            getAppState().setEditedLayerId(isAlreadyExpanded ? null : layerDef?.id);
            this.setState({expandedLayerDef: isAlreadyExpanded ? null : layerDef});
        };

        return (
            <div style={{display: "flex", flexDirection: "column", flex: 1, minHeight: 0}}>
                <Toolbar
                    minimal
                    size="xsmall"
                    key={layerDef.id}
                    css={(theme) => css`background: ${theme.backgroundColor.default};`}
                >
                    {
                        getAppState().getLayerCount() > 1 &&
                        <DragIndicatorVerticalIcon
                            width="fixed"
                            {...provided.dragHandleProps}
                            title={$t("dragtochangeorder")}
                            style={{ cursor: "grabbing" }}
                        />
                    }
                    <Button
                        square
                        onClick={onClickExpand}
                        className={"listitem"}
                        css={(theme: Theme) => css`
                          padding-left: 0;
                          white-space: nowrap;
                          overflow: hidden;
                          text-overflow: ellipsis;
                          color: ${theme.focusAndAccentColor};
                          &:hover {
                            color: ${theme.selectionColor};
                          }
					`}
                    >
                        <ExpansionStatus expanded={isExpanded} />
                        <span>{layerDef.name}</span>
                    </Button>
                    <ToolbarSpacer />
                    {
                        GuiUtil.renderToolbarIcon(
                            layerDef.isHidden ? IconInfo.NOEYE : IconInfo.EYE,
                            $t(layerDef.isHidden ? "show" : "hide"),
                            () => this.toggleHideLayer(layerDef),
                            layerDef.isHidden
                        )
                    }
                    {  GuiUtil.renderToolbarIcon( IconInfo.RENAME, $t("rename"), () => this.renameLayer(layerDef) ) }
                    {  GuiUtil.renderToolbarIcon( IconInfo.DELETE, $t("delete"), () => this.deleteLayer(layerDef) ) }
                </Toolbar>
                {
                    isExpanded &&
                    <LayerPanel key={"lpanel-" + layerDef.id} layerDef={layerDef} isExpanded={isExpanded} />
                }
            </div>
        );
    }

    private onDragEnd = (result) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }
        if (result.source.index !== result.destination.index) {
            getAppState().moveLayerDef(result.source.index, result.destination.index);
            getMapComponent().moveLayer(result.source.index, result.destination.index);
            getAppComponent().refresh();
        }
    }

    render = () => {
        let layerDefs = getAppState().getLayerDefs();

        return (
            <Container orientation="vertical" style={{flex: 1, minHeight: 0}}>
                <Toolbar minimal size="xsmall">
                    {
                        getAppState().hasDatasets() &&
                        <div>
                            <Button size={"xsmall"} onClick={() => getAppComponent().showDialog(<AddLayerDialog />)}>
                                <PlusCircleIcon width="fixed" />
                                {$t("createnewlayer")}
                            </Button>
                            <ToolbarSpacer />
                        </div>
                    }
                </Toolbar>

                <DragDropContext key={`k_${layerDefs}`} onDragEnd={this.onDragEnd}>
                    <Droppable droppableId="droppable" direction="vertical">
                        {(provided, snapshot) => (
                            <div
                                ref={provided.innerRef}
                                style={{display: "flex", flexDirection: "column", flex: 1, minHeight: 0}}
                                {...provided.droppableProps}
                            >
                                {
                                    layerDefs.map((layerDef, index) => (
                                        <Draggable key={`k_${index}`} draggableId={`k_${index}`} index={index}>
                                            {(provided, snapshot) => (
                                                <div
                                                    id={`draggable_layer_${index}`}
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        flex: getAppState().getEditedLayerId() === layerDef.id ? 1 : undefined,
                                                        minHeight: 0
                                                    }}
                                                >
                                                    {this.renderLayer(layerDef, provided)}
                                                </div>
                                            )}
                                        </Draggable>
                                    ))
                                }
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </Container>
        );
    }

}
